import { CommonModule } from '@angular/common';
import { Component, OnInit, OnDestroy, ChangeDetectorRef, ChangeDetectionStrategy, HostListener } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { LabelComponent } from '../../../components/form/label/lable';
import { SelectComponent } from '../../../components/form/select/select';
import { UserService } from '../../../shared/services/user.service';
import {
  PreferencesService,
  PREFERENCE_CATEGORIES,
  PreferenceCategoryMeta,
} from '../../../shared/services/preferences.service';

@Component({
  selector: 'app-preferences',
  standalone: true,
  imports: [CommonModule, RouterLink, FormsModule, LabelComponent, SelectComponent],
  templateUrl: './preferences.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class Preferences implements OnInit, OnDestroy {

  constructor(
    public preferencesService: PreferencesService,
    private userService: UserService,
    private cdr: ChangeDetectorRef,
    private router: Router,
  ) { }

  loading = false;

  /** Auto-save state for the status indicator ('idle' | 'saving' | 'saved' | 'error'). */
  get saveStatus() {
    return this.preferencesService.saveStatus();
  }

  /** Rows rendered below Gender/Attracted To — every category except those
   *  two, which get their own dedicated dropdowns above. */
  readonly categories: PreferenceCategoryMeta[] = PREFERENCE_CATEGORIES;

  // ── Gender (single-select, spec 2a) ──────────────────────────────────────
  genderOptions = [
    { value: 'Male', label: 'Male' },
    { value: 'Female', label: 'Female' },
    { value: 'Trans Man', label: 'Trans Man' },
    { value: 'Trans Woman', label: 'Trans Woman' },
    { value: 'Non-binary', label: 'Non-binary' },
    { value: 'Genderfluid', label: 'Genderfluid' },
    { value: 'Genderqueer', label: 'Genderqueer' },
    { value: 'Agender', label: 'Agender' },
    { value: 'Other', label: 'Other' },
  ];

  // ── Attracted To (multi-select, spec 2b) ─────────────────────────────────
  attractedToOptions = [
    { value: 'Men', label: 'Men' },
    { value: 'Women', label: 'Women' },
    { value: 'Trans Men', label: 'Trans Men' },
    { value: 'Trans Women', label: 'Trans Women' },
    { value: 'Non-binary People', label: 'Non-binary People' },
    { value: 'Multiple Genders', label: 'Multiple Genders' },
    { value: 'All Genders', label: 'All Genders' },
    { value: 'Other', label: 'Other' },
    { value: 'Not Sure', label: 'Not Sure' },
  ];

  attractedToDropdownOpen = false;

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    const target = event.target as HTMLElement;
    if (this.attractedToDropdownOpen && !target.closest('.attracted-to-wrapper')) {
      this.attractedToDropdownOpen = false;
      this.cdr.markForCheck();
    }
  }

  toggleAttractedToDropdown(event: MouseEvent): void {
    event.stopPropagation();
    this.attractedToDropdownOpen = !this.attractedToDropdownOpen;
    this.cdr.markForCheck();
  }

  toggleAttractedTo(event: MouseEvent, value: string): void {
    event.stopPropagation();
    const list = this.preferencesService.draft.attractedTo;
    const idx = list.indexOf(value);
    if (idx === -1) list.push(value);
    else list.splice(idx, 1);
    this.preferencesService.scheduleSave();
    this.cdr.markForCheck();
  }

  isAttractedToSelected(value: string): boolean {
    return this.preferencesService.draft.attractedTo.includes(value);
  }

  removeAttractedTo(value: string): void {
    this.preferencesService.draft.attractedTo =
      this.preferencesService.draft.attractedTo.filter(v => v !== value);
    this.preferencesService.scheduleSave();
    this.cdr.markForCheck();
  }

  onGenderChange(value: string): void {
    this.preferencesService.draft.gender = value ?? '';
    this.preferencesService.scheduleSave();
    this.cdr.markForCheck();
  }

  // ── Lifecycle ─────────────────────────────────────────────────────────────
  ngOnInit(): void {
    // Coming back from a "Select" panel: the draft already holds the latest
    // selections (and is being auto-saved), so show it straight away instead
    // of overwriting it with a server copy that may not include them yet.
    this.loading = !this.preferencesService.loaded;

    this.userService.getUserProfile().subscribe({
      next: (res: any) => {
        this.loading = false;
        if (res.success) {
          const svc = this.preferencesService;
          const sameUser = svc.loaded && svc.loadedUserId === (res.data?.id ?? res.data?._id ?? null);
          // Only refresh from the server when nothing local is still waiting
          // to be saved — otherwise we'd discard the member's latest clicks.
          if (!sameUser || !svc.hasPendingChanges) {
            svc.loadFromProfile(res.data);
          }
        }
        this.cdr.markForCheck();
      },
      error: (err) => {
        this.loading = false;
        console.error(err);
        this.cdr.markForCheck();
      },
    });
  }

  ngOnDestroy(): void {
    // Leaving the screen — send any change still waiting on the debounce.
    this.preferencesService.flush();
  }

  // ── Rows for the 8 multi-select categories ───────────────────────────────
  selectionFor(meta: PreferenceCategoryMeta): string[] {
    return this.preferencesService.draft[meta.key];
  }

  chipsSummary(meta: PreferenceCategoryMeta): string {
    const values = this.selectionFor(meta);
    return values.length ? values.join(', ') : '';
  }

  visibilityFor(meta: PreferenceCategoryMeta): boolean {
    return this.preferencesService.draft.visibility[meta.key];
  }

  toggleRowVisibility(meta: PreferenceCategoryMeta, event: Event): void {
    const checked = (event.target as HTMLInputElement).checked;
    this.preferencesService.draft.visibility[meta.key] = checked;
    this.preferencesService.scheduleSave();
    this.cdr.markForCheck();
  }

  openCategory(meta: PreferenceCategoryMeta): void {
    this.router.navigate(['/dashboard/preferences', meta.slug]);
  }

  retrySave(): void {
    this.preferencesService.retrySave();
  }
}
